import { useMemo } from "react";

import type { TareBridge } from "../internal/NativeModulesTypes";
import { useTare } from "../internal/useTare";

export interface JSTLocalPrefsApi {
  getBoolean(namespace: string, key: string, defaultValue?: boolean): Promise<boolean>;
  setBoolean(namespace: string, key: string, value: boolean): Promise<void>;
  getInt(namespace: string, key: string, defaultValue?: number): Promise<number>;
  setInt(namespace: string, key: string, value: number): Promise<void>;
  getLong(namespace: string, key: string, defaultValue?: number): Promise<number>;
  setLong(namespace: string, key: string, value: number): Promise<void>;
  getString(namespace: string, key: string, defaultValue?: string): Promise<string>;
  setString(namespace: string, key: string, value: string): Promise<void>;
  clearNamespace(namespace: string): Promise<void>;
}

export const createJSTLocalPrefs = (getBridge: () => TareBridge | undefined): JSTLocalPrefsApi => ({
  async getBoolean(namespace: string, key: string, defaultValue?: boolean): Promise<boolean> {
    const bridge = getBridge();
    if (!bridge?.getLocalPrefsBoolean) return defaultValue ?? false;
    return (
      (await bridge.getLocalPrefsBoolean(namespace, key, defaultValue)) ?? defaultValue ?? false
    );
  },
  async setBoolean(namespace: string, key: string, value: boolean): Promise<void> {
    const bridge = getBridge();
    if (!bridge?.setLocalPrefsBoolean) return;
    await bridge.setLocalPrefsBoolean(namespace, key, value);
  },
  async getInt(namespace: string, key: string, defaultValue?: number): Promise<number> {
    const bridge = getBridge();
    if (!bridge?.getLocalPrefsInt) return defaultValue ?? 0;
    return (await bridge.getLocalPrefsInt(namespace, key, defaultValue)) ?? defaultValue ?? 0;
  },
  async setInt(namespace: string, key: string, value: number): Promise<void> {
    const bridge = getBridge();
    if (!bridge?.setLocalPrefsInt) return;
    await bridge.setLocalPrefsInt(namespace, key, value);
  },
  async getLong(namespace: string, key: string, defaultValue?: number): Promise<number> {
    const bridge = getBridge();
    if (!bridge?.getLocalPrefsLong) return defaultValue ?? 0;
    return (await bridge.getLocalPrefsLong(namespace, key, defaultValue)) ?? defaultValue ?? 0;
  },
  async setLong(namespace: string, key: string, value: number): Promise<void> {
    const bridge = getBridge();
    if (!bridge?.setLocalPrefsLong) return;
    await bridge.setLocalPrefsLong(namespace, key, value);
  },
  async getString(namespace: string, key: string, defaultValue?: string): Promise<string> {
    const bridge = getBridge();
    if (!bridge?.getLocalPrefsString) return defaultValue ?? "";
    return (await bridge.getLocalPrefsString(namespace, key, defaultValue)) ?? defaultValue ?? "";
  },
  async setString(namespace: string, key: string, value: string): Promise<void> {
    const bridge = getBridge();
    if (!bridge?.setLocalPrefsString) return;
    await bridge.setLocalPrefsString(namespace, key, value);
  },
  async clearNamespace(namespace: string): Promise<void> {
    const bridge = getBridge();
    if (!bridge?.clearLocalPrefsNamespace) return;
    await bridge.clearLocalPrefsNamespace(namespace);
  },
});

export function useJSTLocalPrefs(): JSTLocalPrefsApi {
  const tareBridge = useTare();
  return useMemo(() => createJSTLocalPrefs(() => tareBridge), [tareBridge]);
}

export default useJSTLocalPrefs;
